/// <reference lib="dom" />

type ChatStreamHandlers = {
  onToken: (token: string) => void;
  onStatus?: (message: string) => void;
  onError?: (message: string) => void;
  onEventId?: (id: string) => void;
};

// SSE can't be read through the Axios-based HttpClient (no streaming support),
// so this talks to the stream endpoint via fetch/ReadableStream directly - the
// one deliberate exception to the "never use fetch" rule for this domain.
function createStreamChatJob(
  getAccessToken: () => Promise<string | undefined>,
  baseUrl: string,
) {
  return async function streamJob(
    jobId: string,
    handlers: ChatStreamHandlers,
    lastEventId?: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const accessToken = await getAccessToken();

    const headers: Record<string, string> = {
      Accept: "text/event-stream",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(lastEventId !== undefined ? { "Last-Event-ID": lastEventId } : {}),
    };

    const response = await fetch(`${baseUrl}/secured/jobs/${jobId}/stream`, {
      method: "GET",
      headers,
      signal,
    });

    if (!response.ok || !response.body) {
      throw new Error(`HTTP ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let currentEvent: string | undefined;
    let currentData: string | undefined;
    let currentId: number | undefined;
    let lastSeenId = lastEventId !== undefined ? Number(lastEventId) : -1;

    const dispatch = () => {
      if (currentId !== undefined) {
        if (currentId <= lastSeenId) {
          currentEvent = currentData = currentId = undefined;
          return;
        }
        lastSeenId = currentId;
        handlers.onEventId?.(String(currentId));
      }

      if (currentEvent === "token" && currentData) {
        handlers.onToken(currentData.replace(/&NewLine;/g, "\n"));
      } else if (currentEvent === "status" && currentData) {
        handlers.onStatus?.(currentData.replace(/&NewLine;/g, "\n"));
      } else if (currentEvent === "error") {
        handlers.onError?.(
          currentData?.replace(/&NewLine;/g, "\n") || "Erreur serveur",
        );
      }
      currentEvent = currentData = currentId = undefined;
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) return;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        if (line === "") {
          dispatch();
        } else if (line.startsWith("id:")) {
          const id = line.slice(3).trim();
          currentId = id ? Number(id) : undefined;
        } else if (line.startsWith("event:")) {
          currentEvent = line.slice(6).trim();
          if (currentEvent === "done") {
            return;
          }
        } else if (line.startsWith("data:")) {
          currentData = line.slice(5).trimEnd();
        }
      }
    }
  };
}

export { createStreamChatJob };

export type { ChatStreamHandlers };
